import classnames from 'classnames/bind'
import dynamic from 'next/dynamic'
import React, { useMemo } from 'react'

import GradientHeaderWithIntro from 'components/shared/GradientHeaderWithIntro'
import Layout from 'components/shared/Layout'
import TriangleImage from 'components/shared/TriangleImage'
import { getMasonryColumns } from 'services/helpers'
import EventCard from './EventCard'
import ReviewCard from './ReviewCard'
import css from './styles.module.scss'
import { Title, Text } from 'components/typo'
const Slider = dynamic(() => import('components/shared/Slider'))

const cx = classnames.bind(css)

const CardsSlider = ({
  items,
  type,
  intro,
  gradientHeader,
  className,
  triangleImage,
  onlyMasonry,
  noBackground,
  isNameBlue,
  noMargin,
  isSimpleCard,
  title,
  upperTitle,
  ...rest
}) => {
  const memoItems = useMemo(() => {
    if (Array.isArray(items)) {
      return items
    }

    return (
      items?.data.map(({ id, attributes }) => ({ id, ...attributes })) || []
    )
  }, [items])

  const masonryColumns = getMasonryColumns(memoItems, [[], [], []])
  const Card = type === 'reviews' ? ReviewCard : EventCard
  const hasItems = memoItems && memoItems.length > 0

  const wrapped = (children) =>
    intro ? (
      <GradientHeaderWithIntro
        intro={intro}
        introProps={{ className: css.intro }}
        gradientHeader={{ ...gradientHeader, gradientPosition: 'center' }}
        fullWidth
        isSimpleCard
      >
        {children}
      </GradientHeaderWithIntro>
    ) : (
      children
    )

  return (
    <div
      className={cx({
        [css.isSimpleCard]: isSimpleCard,
        [css.Reviews]: !isSimpleCard,
      })}
    >
      {!isSimpleCard ? (
        wrapped(
          <>
            <Layout type="wrapper" className={cx({ noSlider: !onlyMasonry })}>
              <div className={css.grid}>
                {hasItems &&
                  masonryColumns.map((group, index) => (
                    <div key={index}>
                      {group.map((item, itemIndex) => (
                        <div
                          key={item.id}
                          className={cx(css.cardContainer, {
                            [css.secondCardMargin]:
                              itemIndex === 1 && !isSimpleCard,
                          })}
                        >
                          <Card
                            className={css.card}
                            item={item}
                            {...rest}
                            isNameBlue={isNameBlue}
                            isSimpleCard={isSimpleCard}
                          />
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            </Layout>
            {hasItems && !onlyMasonry && (
              <Slider className={css.slider} items={memoItems} mobileFullWidth>
                {(item, id, registerRef) => {
                  return (
                    <div
                      key={`${type}-${id}`}
                      ref={registerRef}
                      className={css.cardContainer}
                    >
                      <Card
                        className={css.card}
                        item={item}
                        {...rest}
                        isNameBlue={isNameBlue}
                        isSimpleCard={isSimpleCard}
                      />
                    </div>
                  )
                }}
              </Slider>
            )}
          </>
        )
      ) : (
        <>
          <Layout type="wrapper" className={cx({ noSlider: !onlyMasonry })}>
            <div className={css.header}>
              <Text className={css.upperTitle}>{upperTitle}</Text>
              <Title className={css.title}>{title}</Title>
            </div>
            <div className={css.grid}>
              {hasItems &&
                masonryColumns.map((group, index) =>
                  group.map((item, itemIndex) => (
                    <div
                      key={item.id}
                      className={cx(css.cardContainer, {
                        [css.secondCardMargin]:
                          itemIndex === 1 && !isSimpleCard,
                      })}
                    >
                      <Card
                        className={css.card}
                        item={item}
                        {...rest}
                        isNameBlue={isNameBlue}
                        isSimpleCard={isSimpleCard}
                        withBorder
                      />
                    </div>
                  ))
                )}
            </div>
          </Layout>
          {hasItems && !onlyMasonry && (
            <Slider className={css.slider} items={memoItems} mobileFullWidth>
              {(item, id, registerRef) => {
                return (
                  <div
                    key={`${type}-${id}`}
                    ref={registerRef}
                    className={css.cardContainer}
                  >
                    <Card
                      className={css.card}
                      item={item}
                      {...rest}
                      isNameBlue={isNameBlue}
                      isSimpleCard={isSimpleCard}
                    />
                  </div>
                )
              }}
            </Slider>
          )}
        </>
      )}
      {triangleImage && (
        <TriangleImage
          type="bottom-left"
          className={css.background}
          {...triangleImage}
        />
      )}
    </div>
  )
}

export default CardsSlider
